import { makeObservable, action, computed, runInAction } from "mobx";
import firebaseAgent from "../api/firebaseagent";
import { WishlistItem } from "../models/wishlistItem";
import { Product } from "../models/product";
import { BaseStore } from "./baseStore";
import { serverTimestamp } from "firebase/firestore";
import { RootStore } from "./rootStore";

export default class WishlistStore extends BaseStore<WishlistItem> {
    private LOCAL_STORAGE_KEY = "guest_wishlist";

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            syncWishlist: action,
            toggleWishlist: action,
            itemCount: computed
        });
    }

    protected getService() {
        return firebaseAgent.wishlistService;
    }

    /**
     * Logic: Load user items from DB and merge any local guest items.
     */
    syncWishlist = async (userId?: string) => {
        this.setItemload(false);
        const localData = localStorage.getItem(this.LOCAL_STORAGE_KEY);
        const localItems: WishlistItem[] = localData ? JSON.parse(localData) : [];

        if (userId) {
            const snapshot = await this.getService().getByUser(userId);
            const dbItems = snapshot.docs.map((doc: any) => ({ ...doc.data(), id: doc.id }));

            if (localItems.length > 0) {
                // Transfer guest items to user account in Firestore
                for (const item of localItems) {
                    if (!dbItems.some(db => db.id === item.id)) {
                        await this.getService().register({ ...item, userId, wishlistedAt: serverTimestamp() });
                    }
                }
                localStorage.removeItem(this.LOCAL_STORAGE_KEY);
                const merged = await this.getService().getByUser(userId);
                runInAction(() => { 
                    this.items = merged.docs.map((doc: any) => ({ ...doc.data(), id: doc.id })); 
                });
            } else {
                runInAction(() => { this.items = dbItems; });
            }
        } else {
            runInAction(() => { this.items = localItems; });
        }
        this.setItemload(true);
    };

    /**
     * Toggles item between added and removed
     */
    // toggleWishlist = async (userId: string | undefined, product: Product) => {
    //     // Check if product already exists in current local state
    //     const existingItem = this.items.find(i => i.id === product.id);

    //     if (userId) {
    //         if (existingItem) {
    //             // If exists, remove it (Toggle behavior)
    //             await this.delete(existingItem.id!);
    //             runInAction(() => {
    //                 this.items = this.items.filter(i => i.id !== product.id);
    //             });
    //         } else {
    //             // If not exists, add as a new WishlistItem
    //             const newItem: WishlistItem = { 
    //                 ...product, 
    //                 userId, 
    //                 wishlistedAt: serverTimestamp() 
    //             };
    //             await this.register(newItem);
    //             // BaseStore.register automatically adds to this.items
    //         }
    //     } else {
    //         // Guest Mode Logic
    //         let updated = [...this.items];
    //         if (existingItem) {
    //             updated = updated.filter(i => i.id !== product.id);
    //         } else {
    //             updated.push({ ...product, userId: 'guest' } as WishlistItem);
    //         }
    //         localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(updated));
    //         runInAction(() => { this.items = updated; });
    //     }
    // };

    toggleWishlist = async (userId: string | undefined, product: Product) => {
        // 1. DUPLICATE CHECK: Verify if product ID already exists in MobX state
        const isAlreadyWishlisted = this.items.some(i => i.id === product.id);

        if (userId) {
            try {
                if (isAlreadyWishlisted) {
                    // 2. REMOVE Logic: Call delete on the specific product ID
                    await this.delete(product.id);
                    runInAction(() => {
                        // Update UI immediately by filtering out the removed item
                        this.items = this.items.filter(i => i.id !== product.id);
                    });
                } else {
                    // 3. ADD Logic: Prevent adding if it somehow exists (safety check)
                    const newItem: WishlistItem = { 
                        ...product, 
                        userId, 
                        wishlistedAt: serverTimestamp() 
                    };
                    await this.register(newItem);
                    // Items list is updated automatically by BaseStore.register
                }
            } catch (error) {
                console.error("Wishlist toggle failed:", error);
            }
        } else {
            // Guest Mode Logic with Duplicate Check
            let updated = [...this.items];
            if (isAlreadyWishlisted) {
                updated = updated.filter(i => i.id !== product.id);
            } else {
                updated.push({ ...product, userId: 'guest' } as WishlistItem);
            }
            
            localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(updated));
            runInAction(() => { this.items = updated; });
        }
    };

    get itemCount() {
        return this.items.length;
    }

    isWishlisted = (productId: string) => {
        return this.items.some(i => i.id === productId);
    };
}